import axios from 'axios';
import { CURR_API } from '@/constants/apis';
import { poursFromServer, templateFromPours, administrationsFromServer } from '@/utils/prepourPours';
import { holdRequest, releaseRequest, changeRequest, moveRequest } from '@/utils/prepourStatusRequests';

/**
 * Pre-pour records, one function per kind, keyed by patient (`<account>-<patient>`, or 'sample').
 *
 * Every layout of the pre-pour page (A–E) reaches these records only through here, so moving a kind
 * from the browser to keyon changes this module and the handlers that await it — never a layout.
 * A kind whose backend has landed goes to keyon for a real patient; the sample sheet (no patient)
 * and the kinds still waiting for theirs stay in this browser's localStorage under
 * `ppProto:<kind>:<patient>`. The draft and the auto-fill switch are not here: they stay in the
 * browser for good.
 */

/** A real patient's records live in keyon; the sample sheet's never leave the browser. */
export const isRemote = (pk) => !!pk && pk !== 'sample';

/** A refusal from keyon: `code` is keyon's (forbidden, invalid, short_supply, unavailable …). */
export class PrePourError extends Error {
  constructor(code, message, extra = {}) {
    super(message);
    this.code = code;
    Object.assign(this, extra);
  }
}

async function keyon(method, body) {
  try {
    const res = await axios.post(CURR_API + '/tswebhook.php', { MedicationAdmin: { API_Meth: method, ...body } });
    const data = res && res.data;
    if (data && data.code && data.code !== 'ok') throw new PrePourError(data.code, data.message || data.code, data);
    return data;
  } catch (e) {
    if (e instanceof PrePourError) throw e;
    const data = e && e.response && e.response.data;
    throw new PrePourError((data && data.code) || 'unavailable', (data && data.message) || 'Could not reach the server. Try again.', data || {});
  }
}

/**
 * Signed pours. `ctx` is { account, patient, actorId } for a real patient (keyon GetPours); the
 * sample sheet reads its own.
 */
export async function fetchPours(pk, ctx) {
  if (!isRemote(pk)) return getPours(pk);
  const data = await keyon('GetPours', { accountnumber: ctx.account, patientid: ctx.patient, actorid: ctx.actorId });
  return poursFromServer(data);
}

/**
 * Sign a pour in keyon (PourAndSign). `request` carries signature, palette, skipped and meds[] —
 * see keyon/lib/PrePourSign.php. Resolves to { pourid, take }; rejects with a PrePourError.
 */
export function signPour(ctx, request) {
  return keyon('PourAndSign', { accountnumber: ctx.account, patientid: ctx.patient, actorid: ctx.actorId, ...request });
}

/**
 * What was given from the pours: `pieceId|day|medId|time` → { outcome, witness, notes, actualTime, at, … }.
 * A real patient's come from keyon (GetPrePourAdministrations) and are filed against `pours`.
 */
export async function fetchAdministrations(pk, ctx, pours) {
  if (!isRemote(pk)) return getAdministrations(pk);
  const data = await keyon('GetPrePourAdministrations', { accountnumber: ctx.account, patientid: ctx.patient, actorid: ctx.actorId });
  return administrationsFromServer(data.administrations, pours);
}

/**
 * Record a time block as given in keyon (PrePourAdminister, keyon/lib/PrePourAdminister.php): `request` is
 * { witness, actualTime: 'HH:MM', slots: [{ itemid, outcome, notes }] }. Rejects with a PrePourError.
 */
export function recordAdministration(ctx, request) {
  return keyon('PrePourAdminister', { accountnumber: ctx.account, patientid: ctx.patient, actorid: ctx.actorId, ...request });
}

/**
 * Mark a course Completed in keyon (CompleteMedication, keyon/lib/MedicationCompletion.php): allowed on or after
 * its end date, or any day for a non-prescription medication. Earlier rejects with code 'needs_discontinue'.
 */
export function completeMedication(ctx, { medentryid, date, reason }) {
  return keyon('CompleteMedication', { accountnumber: ctx.account, patientid: ctx.patient, actorid: ctx.actorId, medentryid, date, reason });
}

/*
 * Hold, release, change and time move (#336): a real patient's go through nursing's endpoints, the bodies
 * built in @/utils/prepourStatusRequests. Those endpoints answer in nursing's shapes rather than { code: 'ok' },
 * so each says what success looks like; anything else rejects with a PrePourError carrying keyon's message.
 */
async function nursing(method, body, ok) {
  let data;
  try {
    const res = await axios.post(CURR_API + '/tswebhook.php', { MedicationAdmin: { API_Meth: method, ...body } });
    data = res && res.data;
  } catch (e) {
    const err = e && e.response && e.response.data;
    throw new PrePourError((err && (err.code || err.error)) || 'unavailable', (err && err.message) || 'Could not reach the server. Try again.', err || {});
  }
  if (!data || typeof data !== 'object' || !ok(data)) {
    throw new PrePourError((data && (data.code || data.error)) || 'refused', (data && data.message) || 'keyon did not save the change.', data || {});
  }
  return data;
}

/** Hold `m` (HoldMedication): `hold` is { start, end, times: sheet hours or null for all, reason }. */
export function holdMedication(ctx, m, hold, now = new Date()) {
  return nursing('HoldMedication', holdRequest(ctx, m, hold, now), (d) => d.results === 'Updated');
}
/** Release `m`'s hold (ClearCurrentMedicationHold): its held times from today on are due again. */
export function releaseHold(ctx, m, reason = '') {
  return nursing('ClearCurrentMedicationHold', releaseRequest(ctx, m, reason), (d) => d.status === '200-Successfull');
}
/** Change `m`'s dose / frequency / times (ChangeOrderForMedLogTimes): `change` is { dose, freq, times, reason }. */
export function changeMedication(ctx, m, change, now = new Date()) {
  return nursing('ChangeOrderForMedLogTimes', changeRequest(ctx, m, change, now), (d) => d.results === 'Updated' || d.results === 'Insert');
}
/** Move `m`'s times (InsertUpdateMedLogTimes): `move` is { times: the sheet's hours after the move, reason }. */
export function moveTimes(ctx, m, move, now = new Date()) {
  return nursing('InsertUpdateMedLogTimes', moveRequest(ctx, m, move, now), (d) => d.results === 'Updated');
}
/** A real patient's holds from today on (GetPrePourHolds): [{ medentryid, start, end, times: HH:MM, reason }]. */
export async function fetchHolds(ctx) {
  const data = await keyon('GetPrePourHolds', { accountnumber: ctx.account, patientid: ctx.patient, actorid: ctx.actorId });
  return Array.isArray(data.holds) ? data.holds : [];
}
/** A real patient's History (GetMedStatusEvents), oldest first. */
export async function fetchStatusEvents(ctx) {
  const data = await keyon('GetMedStatusEvents', { accountnumber: ctx.account, patientid: ctx.patient, actorid: ctx.actorId });
  return Array.isArray(data.events) ? data.events : [];
}

/** The next pour's template: a real patient's is the last signed pour (`pours`); the sample keeps its own. */
export function templateFor(pk, pours) {
  return isRemote(pk) ? templateFromPours(pours) : getTemplate(pk);
}

function key(kind, patientKey) {
  return `ppProto:${kind}:${patientKey}`;
}
function read(kind, patientKey, dflt) {
  try {
    const v = localStorage.getItem(key(kind, patientKey));
    return v ? JSON.parse(v) : dflt;
  } catch (e) {
    return dflt;
  }
}
function write(kind, patientKey, value) {
  try {
    localStorage.setItem(key(kind, patientKey), JSON.stringify(value));
  } catch (e) { /* quota / private mode */ }
}

// signed pours — the lock
export const getPours = (pk) => read('pours', pk, []);
// a real patient's pours change only through PourAndSign; edits made in the page are not written back
export const savePours = (pk, pours) => { if (!isRemote(pk)) write('pours', pk, pours); };

// what each pre-poured slot's administration recorded: outcome, witness, actual time, notes
export const getAdministrations = (pk) => read('admin', pk, {});
// a real patient's change only through PrePourAdminister
export const saveAdministrations = (pk, admin) => { if (!isRemote(pk)) write('admin', pk, admin); };

// tablets on hand after a pour, by medication id — the sample sheet's only: a real patient's count is
// medications.available, which PourAndSign takes from and GetPatientMeds returns
export const getOnHand = (pk) => (isRemote(pk) ? {} : read('onhand', pk, {}));
export const saveOnHand = (pk, onHand) => write('onhand', pk, onHand);

// discontinue requests, by medication id
export const getDiscontinues = (pk) => read('dc', pk, {});
export function saveDiscontinue(pk, medId, rec) {
  const all = getDiscontinues(pk);
  all[medId] = rec;
  write('dc', pk, all);
}

// status record (Hold / Change / Completed …), holds and per-time doses, by medication id - the sample sheet's
// only: a real patient's holds and changes are keyon's (fetchHolds, fetchStatusEvents)
export const getStatuses = (pk) => (isRemote(pk) ? {} : read('status', pk, {}));
export function saveStatus(pk, medId, rec) {
  if (isRemote(pk)) return;
  const all = getStatuses(pk);
  all[medId] = rec;
  write('status', pk, all);
}

// every status change, dated. A real patient's History is keyon's (fetchStatusEvents), except a discontinue's: the
// discontinue itself is still recorded in this browser until its sign-off moves to keyon, and so is its entry.
const LOCAL_CHANGE_KINDS = ['Discontinue', 'DC-pending'];
const localKinds = (pk, changes) => (isRemote(pk) ? changes.filter((c) => LOCAL_CHANGE_KINDS.includes(c.status)) : changes);
export const getChanges = (pk) => localKinds(pk, read('changes', pk, []));
export const saveChanges = (pk, changes) => write('changes', pk, localKinds(pk, changes));

// the shape of the last signed pour, reused as defaults
export const getTemplate = (pk) => read('template', pk, null);
export const saveTemplate = (pk, template) => write('template', pk, template);

/** The prototype's "Reset local data" for one patient. */
export function clearLocal(pk) {
  ['pours', 'admin', 'onhand', 'dc', 'template', 'changes'].forEach((k) => {
    try { localStorage.removeItem(key(k, pk)); } catch (e) { /* private mode */ }
  });
}
